import {
  getAttachmentLabel,
  shouldShowMessageText,
  stripImageUrlsFromText,
} from '@/modules/chat-matrix-fast/utils/fileUtils'
import { markdownToPlainText } from '@/modules/chat-matrix-fast/utils/messageRichText'

/** Цитата в формате Битрикс: `-Имя\n-Текст\n-...` */
export interface ParsedQuote {
  sender: string
  text: string
  originalText: string
}

export function parseQuoteFromContent(content: string): { quote: ParsedQuote | null; remainingText: string } {
  if (!content || typeof content !== 'string') {
    return { quote: null, remainingText: content || '' }
  }

  const quotePattern = /^-([^\n]+)\n-([^\n]+)\n-/

  const match = content.match(quotePattern)

  if (!match) {
    return { quote: null, remainingText: content }
  }

  const sender = match[1].trim()
  const quoteText = match[2].trim()
  const remainingText = content.substring(match[0].length).trim()

  return {
    quote: {
      sender,
      text: quoteText,
      originalText: match[0],
    },
    remainingText,
  }
}

/** Сохраняет блок цитаты ответа при редактировании текста сообщения. */
export function buildEditedMessageContent(
  existingContent: string,
  editedText: string,
): string {
  const trimmedText = editedText.trim()
  const { quote } = parseQuoteFromContent(existingContent)

  if (quote) {
    return trimmedText ? `${quote.originalText}\n${trimmedText}` : quote.originalText
  }

  return trimmedText
}

/** Текст сообщения без вложенной цитаты — для ответа на reply. */
export function getPlainTextForReplyQuote(content: string | null | undefined): string {
  const raw = String(content || '').trim()
  if (!raw) {
    return 'Сообщение удалено'
  }

  const { remainingText } = parseQuoteFromContent(raw)
  const plain = markdownToPlainText((remainingText || raw).trim()).replace(/\s+/g, ' ')

  return plain || 'Сообщение удалено'
}

/** Текст сообщения для буфера обмена: без цитаты, markdown и служебных URL картинок. */
export function getMessageClipboardText(message: {
  message?: string | null
  content?: string | null
  is_deleted?: boolean
  attachments?: Array<{ file: string; mime_type?: string; name?: string }> | null
}): string {
  if (message.is_deleted) {
    return ''
  }

  const raw = String(message.message || message.content || '')
  const { remainingText } = parseQuoteFromContent(raw)
  const plain = markdownToPlainText(remainingText).trim()
  const visibleText = stripImageUrlsFromText(plain)

  if (visibleText && shouldShowMessageText(visibleText, message.attachments ?? undefined)) {
    return visibleText
  }

  return (message.attachments ?? [])
    .map(attachment => getAttachmentLabel(attachment, plain))
    .filter(Boolean)
    .join('\n')
}
